/**
 * R9 §3.2's second source — the last document that validated, stored whole under
 * `selogate.<stage>.client-config` beside the other device-local preferences.
 *
 * **`localStorage`, not the IndexedDB snapshot store, and that is a decision (D328, 🔸 R9 §3.2).**
 * The shell needs the cached flags *synchronously at boot* to route without a flash of the wrong
 * surface, and IndexedDB cannot answer synchronously. **Every read and write is wrapped** so a
 * missing or refused store reads as *no cache* — `pwa/updateAlerts.ts`'s posture, for the same
 * reason: WebKit serves `localStorage` from the process D309 says can be dead, and a throw here
 * must land on the defaults rather than on the boot.
 *
 * The record holds nothing `device-cache.md`'s security column would refuse — no credential, no
 * grant, no identifier, no coordinate — and **that is asserted by the schema, not by this row**:
 * `packages/core`'s document type has no field that could carry one.
 */

import type { ClientConfigDocument } from '@selogate/core'
import { STAGE } from '../auth/config'
import { readClientConfigDocument } from './document'

const cacheKey = (stage: string): string => `selogate.${stage}.client-config`

export interface CachedClientConfig {
  readonly document: ClientConfigDocument
  /** `Date.now()` when the fetch that produced it landed. */
  readonly fetchedAt: number
}

export function readCachedClientConfig(stage: string = STAGE): CachedClientConfig | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(cacheKey(stage))
    if (typeof raw !== 'string') return undefined
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return undefined
    const { document: rawDocument, fetchedAt } = parsed as { document?: unknown; fetchedAt?: unknown }
    const document = readClientConfigDocument(rawDocument)
    if (document === undefined || typeof fetchedAt !== 'number' || !Number.isFinite(fetchedAt)) {
      return undefined
    }
    return { document, fetchedAt }
  } catch {
    return undefined
  }
}

export function writeCachedClientConfig(
  document: ClientConfigDocument,
  fetchedAt: number,
  stage: string = STAGE,
): void {
  try {
    const record: CachedClientConfig = { document, fetchedAt }
    globalThis.localStorage?.setItem(cacheKey(stage), JSON.stringify(record))
  } catch {
    /* ignore — see the module header */
  }
}

/** Test seam. */
export function clearCachedClientConfig(stage: string = STAGE): void {
  try {
    globalThis.localStorage?.removeItem(cacheKey(stage))
  } catch {
    /* ignore */
  }
}
